import {forwardRef, ReactNode} from 'react';

type Props = {
  /** Pinned header region (RISE wordmark + section pill + progress). */
  header: ReactNode;
  /** Optional fixed CTA. It anchors to the card bottom on desktop (the
   *  card is the containing block via osaShell.css `transform: translateZ(0)`)
   *  and to the viewport on mobile. */
  footer?: ReactNode;
  /** Scroll-body content. */
  children: ReactNode;
};

/**
 * Shared OSA funnel shell: the centered card (osaShell.css `.osa-shell`) with
 * a pinned header, an internally-scrolling body, and an optional fixed footer.
 *
 * This is the single source of the markup contract that osaShell.css depends
 * on by DOM position — `.osa-shell > div:first-child` (header, flex:0) and
 * `.osa-shell > .survey-page-transition` (scroll body, flex:1). The survey
 * screens (InsomniaSurvey) render the same structure inline; the standalone
 * OSA screens (screening, paywall) use this so they don't hand-copy — and
 * silently drift from — that contract.
 *
 * The ref is forwarded to the scroll body so callers can reset its scroll
 * position (e.g. on a step change) — the body, not the window, is the
 * scroller on desktop (the card is overflow:hidden).
 */
export const OsaShell = forwardRef<HTMLDivElement, Props>(({header, footer, children}, scrollRef) => (
  <div className="container osa-shell">
    <div>{header}</div>
    <div className="survey-page-transition" ref={scrollRef}>
      {children}
    </div>
    {footer}
  </div>
));

OsaShell.displayName = 'OsaShell';
